import { EntityPhotosComponent } from '@shared/components/entity-photos/entity-photos.component';
import { Component, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { Person } from '../models/person.model';
import { MembershipStatusBadgeComponent } from '@shared/components/membership-status-badge/membership-status-badge.component';
import { SpiritualStageBadgeComponent } from '@shared/components/spiritual-stage-badge/spiritual-stage-badge.component';
import { MinistryStatusBadgeComponent } from '@shared/components/ministry-status-badge/ministry-status-badge.component';
import { AgeStatusBadgeComponent } from '@shared/components/age-status-badge/age-status-badge.component';
import { MinistryStatus } from '@core/api/generated/api-client';
import { DeleteConfirmDialogComponent, DeleteConfirmDialogData } from '@features/user/components/shared/delete-confirm-dialog/delete-confirm-dialog.component';
import { modalDialogConfig } from '@shared/components/modal-shell/modal-dialog.config';
import { NotificationService } from '@shared/services/notification.service';
import { PersonService } from '../services/person.service';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import { filter, map } from 'rxjs/operators';
import { PersonStatusDialogComponent, PersonStatusDialogResult } from './person-status-dialog.component';

/**
 * Componente de encabezado de persona
 * 
 * Muestra información principal de una persona incluyendo:
 * - Foto de perfil
 * - Datos personales (nombre, fecha de nacimiento, edad)
 * - Información de comunidad y ministerio
 * - Badges de estado (spiritual stage, membership status, age status, etc)
 * - Información de contacto
 * - Menú de acciones
 */
@Component({
  selector: 'app-person-header',
  standalone: true,
  host: { '[class.profile-header]': "layout === 'profile'" },
  imports: [EntityPhotosComponent,
    CommonModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    MatDividerModule,
    MatDialogModule,
    MatTooltipModule,
    MembershipStatusBadgeComponent,
    SpiritualStageBadgeComponent,
    MinistryStatusBadgeComponent,
    AgeStatusBadgeComponent,
  ],
  templateUrl: './person-header.component.html',
  styleUrl: './person-header.component.scss'
})
export class PersonHeaderComponent implements OnInit {
  @Input() person!: Person;
  @Input() age?: number;
  @Input() layout: 'header' | 'profile' = 'header';
  @Input() collapseProgress = 0;
  @Input() ministryActivityLines: { activities: string[]; units: { id?: string; name: string }[]; ministryStatus?: MinistryStatus }[] = [];
  @Output() statusChanged = new EventEmitter<void>();
  @Output() homeAttendanceChanged = new EventEmitter<void>();

  photoLoaded = true;
  private readonly userFullInfoService = inject(UserFullInfoService);
  readonly canManageMemberData$ = this.userFullInfoService.canManageMemberData$;
  readonly isAdmin$ = this.userFullInfoService.getUserInfo$().pipe(map(user => user?.isAdmin === true));

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly personService: PersonService,
    private readonly notificationService: NotificationService,
  ) { }

  ngOnInit(): void {
    if (!this.person) {
      throw new Error('Person input is required');
    }
  }

  // ==================== PHOTO METHODS ====================

  getPersonPhoto(): string {
    return this.person.photo || '';
  }

  hasPhoto(): boolean {
    return !!this.person.photo && this.photoLoaded;
  }

  onPhotoError(): void {
    this.photoLoaded = false;
  }

  // ==================== DISPLAY METHODS ====================

  getFullName(): string {
    return `${this.person.firstName} ${this.person.lastName}`;
  }

  formatDateOfBirth(date: Date): string {
    return date.toLocaleDateString('es-AR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  // ==================== MINISTRY METHODS ====================

  get ministryStatus(): MinistryStatus | undefined {
    const s = this.currentMinistry?.status;
    return s ? (s as MinistryStatus) : undefined;
  }

  get currentMinistry(): Person['ministry'] | undefined {
    const ministry = this.person.ministry;
    return ministry?.status === 'Terminated' ? undefined : ministry;
  }

  // ==================== NAVIGATION METHODS ====================

  navigateToCommunity(communityId: string): void {
    this.router.navigate(['/units', communityId]);
  }

  navigateToMinistryUnit(event: MouseEvent, unitId: string): void {
    event.preventDefault();
    event.stopPropagation();
    void this.router.navigate(['/units', unitId]);
  }

  // ==================== MENU ACTIONS ====================

  onMenuAction(action: string): void {
    switch (action) {
      case 'edit':
        this.router.navigate(['/people', this.person.id, 'edit']);
        break;
      case 'events':
        this.router.navigate([...this.getPersonBasePath(), 'events']);
        break;
      case 'community-transfer':
        this.router.navigate(['/people', this.person.id, 'community-history']);
        break;
      case 'status':
        this.openStatusDialog();
        break;
      case 'home-attendance':
        this.toggleHomeAttendance();
        break;
      case 'create-user':
        this.router.navigate(['/users/new'], { queryParams: { personId: this.person.id } });
        break;
      case 'delete':
        this.openDeleteDialog();
        break;
    }
  }

  openStatusDialog(): void {
    if (!this.userFullInfoService.canManageMemberDataSnapshot()) {
      return;
    }

    this.dialog.open(PersonStatusDialogComponent, modalDialogConfig({
      data: { status: this.person.status },
      size: 'sm',
      layout: 'form',
    })).afterClosed().pipe(
      filter((result): result is PersonStatusDialogResult => !!result)
    ).subscribe(result => {
      this.personService.updateStatus(this.person.id, result).subscribe({
        next: person => {
          this.person = person;
          this.notificationService.success('Estado actualizado correctamente.');
          this.statusChanged.emit();
        },
        error: err => {
          this.notificationService.error(this.getRequestErrorMessage(err, 'No se pudo actualizar el estado.'));
        }
      });
    });
  }

  private toggleHomeAttendance(): void {
    if (!this.userFullInfoService.canManageMemberDataSnapshot()) {
      return;
    }

    const isHomeAttended = !this.person.isHomeAttended;
    this.personService.updateHomeAttendance(this.person.id, isHomeAttended).subscribe({
      next: () => {
        this.person = { ...this.person, isHomeAttended };
        this.notificationService.success(
          isHomeAttended
            ? 'Miembro marcado como atendido en el hogar.'
            : 'Atención en el hogar quitada.'
        );
        this.homeAttendanceChanged.emit();
      },
      error: error => this.notificationService.error(
        this.getRequestErrorMessage(error, 'No se pudo actualizar la atención en el hogar.')
      ),
    });
  }

  private getPersonBasePath(): string[] {
    const allParams = this.route.snapshot.pathFromRoot.reduce(
      (acc, route) => ({ ...acc, ...route.params }), {} as Record<string, string>
    );
    return allParams['unit-id']
      ? ['/units', allParams['unit-id'], 'members', this.person.id]
      : ['/people', this.person.id];
  }

  private openDeleteDialog(): void {
    if (this.isCurrentUserPerson()) {
      this.notificationService.error('No podés eliminar tu propia persona.');
      return;
    }

    const dialogRef = this.dialog.open(DeleteConfirmDialogComponent, modalDialogConfig({
      data: {
        title: 'Eliminar persona',
        message: `¿Estás seguro de que querés eliminar <strong>${this.getFullName()}</strong>? Esta acción no se puede deshacer.`,
        confirmButtonText: 'Eliminar',
        requiresReason: true,
      } as DeleteConfirmDialogData,
      size: 'sm',
      layout: 'confirm',
      tone: 'danger',
    }));

    dialogRef.afterClosed().pipe(
      filter(result => result?.confirmed === true)
    ).subscribe(result => {
      const reason: string = result.deletionReason;
      this.personService.deletePerson(this.person.id, reason).subscribe({
        next: () => {
          this.notificationService.success(`La persona "${this.getFullName()}" fue eliminada correctamente.`);
          if (this.person.communityId) {
            this.router.navigate(['/units', this.person.communityId]);
            return;
          }

          this.router.navigate(['/']);
        },
        error: (err) => {
          const message = this.getDeleteErrorMessage(err);
          this.notificationService.error(message);
        }
      });
    });
  }

  private isCurrentUserPerson(): boolean {
    return this.userFullInfoService.getUserInfoSnapshot()?.personId === this.person.id;
  }

  private getDeleteErrorMessage(err: unknown): string {
    return this.getRequestErrorMessage(err, 'No se pudo eliminar la persona.');
  }

  private getRequestErrorMessage(err: unknown, fallback: string): string {
    if (!err || typeof err !== 'object') {
      return fallback;
    }

    const error = err as {
      error?: { detail?: string; title?: string };
      result?: { detail?: string; title?: string };
      response?: string;
      detail?: string;
      message?: string;
    };

    if (error.error?.detail) {
      return error.error.detail;
    }

    if (error.result?.detail) {
      return error.result.detail;
    }

    if (error.detail) {
      return error.detail;
    }

    if (error.response) {
      try {
        const parsed = JSON.parse(error.response) as { detail?: string; title?: string };
        if (parsed.detail) {
          return parsed.detail;
        }

        if (parsed.title) {
          return parsed.title;
        }
      } catch {
        // Ignore parse errors and continue with other fallbacks.
      }
    }

    if (error.error?.title) {
      return error.error.title;
    }

    if (error.result?.title) {
      return error.result.title;
    }

    if (error.message && error.message !== 'An unexpected server error occurred.') {
      return error.message;
    }

    return fallback;
  }

}
